import FtpeMasqueToggle from '../FtpeMasqueToggle';
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription } from '../ui/alert';
import { CheckCircle, FileText, Info, Save, Calculator, RefreshCw, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { ContributionAllocation } from './constants';
import ResultatDocuments from './ResultatDocuments';
import ResultatPayment from './ResultatPayment';
import ResultatFTPE from './ResultatFTPE';
import ResultatSignature from './ResultatSignature';

function UnsavedCalculationPreview({ result, technique, identite, isFtpeMember, saving, handleSaveSimulation, setActiveTab }) {
  const contributionHt = result?.calcul?.contribution_ht || result?.contribution_ht || 0;
  const coutConstruction = result?.calcul?.cout_construction_ht || technique.cout_construction_ht || 0;
  const tauxFinal = result?.calcul?.taux_final || result?.calcul?.taux_base || 0;

  return (
    <div className="space-y-6" data-testid="unsaved-calculation-preview">
      <Card className="border-2 border-amber-200 bg-amber-50/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
            <Calculator className="h-5 w-5" />
            Estimation calculee
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-xl border">
              <div className="text-sm text-gray-500">Cout construction HT</div>
              <div className="text-xl font-bold text-gray-800" data-testid="preview-cout-construction">
                {coutConstruction.toLocaleString('fr-FR')} EUR
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border">
              <div className="text-sm text-gray-500">Taux applique</div>
              <div className="text-xl font-bold text-gray-800" data-testid="preview-taux">
                {typeof tauxFinal === 'number' ? tauxFinal.toFixed(2) : tauxFinal}%
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-100 to-green-100 p-5 rounded-xl border-2 border-emerald-300">
            <div className="text-center">
              <div className="text-sm text-emerald-700 font-medium mb-1">MONTANT GLOBAL - CONTRIBUTION SOLIDAIRE PROPOSEE</div>
              <div className="text-3xl font-bold text-emerald-800" data-testid="preview-contribution-ht">
                {contributionHt.toLocaleString('fr-FR')} EUR
              </div>
            </div>
          </div>

          {!isFtpeMember && identite.qualite !== 'particulier' && (
            <div className="p-3 rounded-xl border-l-4 border-blue-600 bg-blue-50">
              <div className="text-xs font-bold text-blue-700 uppercase mb-1">Adhesion partenaire</div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-700">Cotisation annuelle FTPE</span>
                <span className="font-bold text-blue-700">200,00 EUR</span>
              </div>
            </div>
          )}

          {!isFtpeMember && identite.qualite !== 'particulier' && (
            <div className="p-3 rounded-xl bg-indigo-900 text-white">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold uppercase">Total indicatif du parcours cooperatif</span>
                <span className="text-lg font-bold">{(contributionHt + 200).toLocaleString('fr-FR')} EUR</span>
              </div>
            </div>
          )}

          {result?.affectation && <ContributionAllocation affectation={result.affectation} />}

          {result?.calcul?.reductions?.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Reductions appliquees :</div>
              {result.calcul.reductions.map((r, idx) => (
                <div key={idx} className="flex justify-between text-sm bg-white p-2 rounded-lg border">
                  <span className="text-gray-600">{r.motif}</span>
                  <span className="text-emerald-600 font-medium">-{r.taux}%</span>
                </div>
              ))}
            </div>
          )}

          <Separator />

          <Alert className="border-amber-300 bg-amber-50">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Ceci est une estimation. Pour generer les documents et obtenir une reference, 
              cliquez sur <strong>Creer la souscription</strong> ci-dessous.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setActiveTab('technique')}
              className="flex-1"
              data-testid="btn-back-technique"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Modifier les donnees
            </Button>
            <Button
              onClick={handleSaveSimulation}
              disabled={saving}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold"
              data-testid="btn-create-souscription-resultat"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Creer la souscription
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ResultatTab({
  onPlanChange,
  savedSimulation,
  identite,
  operation: operation,
  technique,
  intervenants: intervenants,
  result,
  isFtpeMember,
  downloadingDoc,
  otpSent,
  signingInProgress,
  signatureMethod,
  signatureDestination,
  otpCode,
  setOtpCode,
  confirmingVirement,
  confirmingFTPE,
  saving,
  handleSaveSimulation,
  setActiveTab,
  handleDownloadDocument,
  handlePrintDocumentsList,
  handleValidateSignature,
  handleInitiateSignature,
  handleResendOTP,
  handleConfirmVirement,
  handleConfirmFTPEPayment
}) {
  // Si la simulation est enregistree, afficher le resultat complet
  if (savedSimulation && savedSimulation.reference) {
    return (
      <div className="space-y-6">
        <Card className="border-2 border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-emerald-700">
              <CheckCircle className="h-5 w-5" />
              Simulation enregistree
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Reference + Signature badge */}
            <div className="bg-white p-4 rounded-xl border">
              <div className="text-sm text-gray-500">Reference de souscription</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="reference-souscription">
                {savedSimulation.reference}
              </div>
              {savedSimulation.signature?.statut === 'signed' && savedSimulation.signature?.signed_at && (
                <Badge className="mt-2 bg-green-500 font-semibold">Signe le {new Date(savedSimulation.signature.signed_at).toLocaleString('fr-FR')}</Badge>
              )}
            </div>

            {/* Montants */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl border">
                <div className="text-sm text-gray-500">Cout construction</div>
                <div className="text-xl font-bold text-gray-800">
                  {(savedSimulation.technique?.cout_construction_ht || technique.cout_construction_ht || 0).toLocaleString('fr-FR')} EUR
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border">
                <div className="text-sm text-gray-500">Contribution DOMIUM</div>
                <div className="text-xl font-bold text-emerald-700">
                  {(savedSimulation.calcul?.contribution_ht || savedSimulation.calcul?.prime_ht || 0).toLocaleString('fr-FR')} EUR
                </div>
              </div>
            </div>

            {/* Montant Global */}
            <div className="bg-gradient-to-r from-emerald-100 to-green-100 p-4 rounded-xl border-2 border-emerald-300">
              <div className="flex justify-between items-center">
                <div className="text-sm text-emerald-700 font-medium">MONTANT GLOBAL - CONTRIBUTION SOLIDAIRE PROPOSEE</div>
                <div className="text-2xl font-bold text-emerald-800">
                  {(savedSimulation.calcul?.contribution_ht || savedSimulation.calcul?.prime_ht || 0).toLocaleString('fr-FR')} EUR
                </div>
              </div>
            </div>

            <FtpeMasqueToggle />
            {/* Bloc Adhesion partenaire FTPE */}
            {!isFtpeMember && identite.qualite !== 'particulier' && (
              <div className="p-4 rounded-xl border-l-4 border-blue-600 bg-blue-50" data-testid="ftpe-adhesion-block">
                <div className="text-xs font-bold text-blue-700 uppercase mb-2">Adhesion partenaire</div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Cotisation annuelle FTPE</span>
                  <span className="font-bold text-blue-700">200,00 EUR</span>
                </div>
                <p className="text-xs text-gray-500 italic mt-2">
                  Paiement direct a la FTPE -- (COPPAM -- programme heberge par O'SCOP -- n'encaisse pas cette cotisation)
                </p>
              </div>
            )}

            {/* Total indicatif du parcours cooperatif */}
            {!isFtpeMember && identite.qualite !== 'particulier' && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-900 to-indigo-800 text-white" data-testid="ftpe-total-parcours-block">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold uppercase">Total indicatif du parcours cooperatif</span>
                  <span className="text-lg font-bold">
                    {((savedSimulation.calcul?.contribution_ht || savedSimulation.calcul?.prime_ht || 0) + 200).toLocaleString('fr-FR')} EUR
                  </span>
                </div>
                <div className="text-xs opacity-80 mt-1">
                  (Montant Global {(savedSimulation.calcul?.contribution_ht || savedSimulation.calcul?.prime_ht || 0).toLocaleString('fr-FR')} EUR + Cotisation FTPE 200,00 EUR)
                </div>
              </div>
            )}

            <ContributionAllocation affectation={savedSimulation.affectation} />

            <Separator />

            {/* QR Code de verification si signe */}
            {savedSimulation.signature?.statut === 'signed' && savedSimulation.signature?.qr_code && (
              <Card className="border-2 border-green-200 bg-green-50/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 rounded-lg border">
                      <img
                        src={`data:image/png;base64,${savedSimulation.signature.qr_code}`}
                        alt="QR Code verification"
                        className="w-20 h-20"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-500">Code de verification</div>
                      <div className="font-mono text-lg font-bold text-green-700">
                        {savedSimulation.signature?.verification_code || '-'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Signe par {savedSimulation.signature?.signer_name || 'Inconnu'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Separator />

            {/* 1. Documents */}
            <ResultatDocuments
              savedSimulation={savedSimulation}
              identite={identite}
              downloadingDoc={downloadingDoc}
              handleDownloadDocument={handleDownloadDocument}
              handlePrintDocumentsList={handlePrintDocumentsList}
            />

            <Separator />

            {/* 2. Paiement DOS */}
            <ResultatPayment
              onPlanChange={onPlanChange}
              savedSimulation={savedSimulation}
              result={result}
              confirmingVirement={confirmingVirement}
              handleConfirmVirement={handleConfirmVirement}
            />

            <Separator />

            {/* 3. Paiement FTPE — masqué si le souscripteur est déjà adhérent */}
            {!isFtpeMember && (
            <ResultatFTPE
              savedSimulation={savedSimulation}
              identite={identite}
              confirmingFTPE={confirmingFTPE}
              handleConfirmFTPEPayment={handleConfirmFTPEPayment}
            />
            )}

            {/* 4. Signature electronique */}
            <ResultatSignature
              savedSimulation={savedSimulation}
              identite={identite}
              otpSent={otpSent}
              signingInProgress={signingInProgress}
              signatureMethod={signatureMethod}
              signatureDestination={signatureDestination}
              otpCode={otpCode}
              setOtpCode={setOtpCode}
              handleInitiateSignature={handleInitiateSignature}
              handleValidateSignature={handleValidateSignature}
              handleResendOTP={handleResendOTP}
            />

            {/* 5. Bouton SAUVEGARDER */}
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => toast.success('Souscription sauvegardee ! Vous pouvez la reprendre depuis le tableau des membres.')}
                className="w-full h-14 border-2 border-blue-300 text-blue-700 hover:bg-blue-50 font-semibold"
                data-testid="btn-sauvegarder"
              >
                <Save className="h-5 w-5 mr-2" />
                SAUVEGARDER
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Enregistre la souscription pour la reprendre plus tard depuis le tableau des membres.
              </p>
            </div>
          </CardContent>
        </Card>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Prochaines etapes :</strong> 1) Telechargez et verifiez les documents.
            2) Effectuez le virement bancaire avec la reference indiquee.
            3) Cliquez sur SIGNER pour valider et recevoir votre attestation.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Si un calcul est disponible (mais pas encore enregistre), montrer l'apercu
  const hasCalculation = result && (result.calcul?.contribution_ht || result.contribution_ht);
  if (hasCalculation) {
    return (
      <UnsavedCalculationPreview
        result={result}
        technique={technique}
        identite={identite}
        isFtpeMember={isFtpeMember}
        saving={saving}
        handleSaveSimulation={handleSaveSimulation}
        setActiveTab={setActiveTab}
      />
    );
  }

  // Aucun calcul disponible
  return (
    <Card>
      <CardContent className="py-12 text-center text-gray-500">
        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Aucune simulation calculee</p>
        <p className="text-sm mt-2">Renseignez le cout de construction dans l'onglet <strong>Technique</strong> pour obtenir un calcul automatique.</p>
        <Button
          variant="outline"
          onClick={() => setActiveTab('technique')}
          className="mt-4"
          data-testid="btn-goto-technique"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Aller a l'etape Technique
        </Button>
      </CardContent>
    </Card>
  );
}
